import { useMemo, useState } from 'react';
import { FieldShell } from './FieldShell';
import type { FieldComponentProps } from './types';

const parseSelected = (v: unknown): string[] => {
  if (typeof v !== 'string') return [];
  return v.split(',').map((t) => t.trim()).filter(Boolean);
};

const joinSelected = (parts: string[]): string => parts.join(',');

export const MultiSelectField = ({ field, value, error, onChange }: FieldComponentProps) => {
  const options = field.colOptions || [];
  const showSearch = field.constraints?.show_search_widget === true;
  const [query, setQuery] = useState('');
  const selected = parseSelected(value);

  const filtered = useMemo(() => {
    if (!showSearch || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) =>
      (o.display_title || o.title).toLowerCase().includes(q) || o.title.toLowerCase().includes(q),
    );
  }, [options, query, showSearch]);

  const toggle = (title: string) => {
    const next = selected.includes(title)
      ? selected.filter((t) => t !== title)
      : [...selected, title];
    onChange(joinSelected(next));
  };

  return (
    <FieldShell field={field} error={error}>
      {showSearch && (
        <input
          type="text"
          placeholder="Filter options…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="form-input mb-2"
        />
      )}
      <div className="space-y-1 max-h-56 overflow-y-auto rounded-lg border border-rgf-gray-300 px-3 py-2">
        {filtered.length === 0 ? (
          <div className="text-sm text-rgf-gray-500">No options.</div>
        ) : (
          filtered.map((o) => {
            const checked = selected.includes(o.title);
            return (
              <label key={o.title} className="flex items-center gap-2 cursor-pointer text-sm py-1">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-rgf-gray-300 text-rgf-primary-600 focus:ring-rgf-primary-500"
                  checked={checked}
                  onChange={() => toggle(o.title)}
                />
                <span className="text-rgf-gray-800">{o.display_title || o.title}</span>
              </label>
            );
          })
        )}
      </div>
      {selected.length > 0 && (
        <p className="mt-1 text-xs text-rgf-gray-500">
          Selected: {selected.length}
        </p>
      )}
    </FieldShell>
  );
};
